import { lazy, Suspense, useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Plus, Send, Eye, MousePointerClick, Calendar, Users, X, FileText, Edit2, Trash2,
  MailX, ShieldAlert, AlertTriangle, List, RefreshCw, MoreHorizontal, Mail, HelpCircle, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";
import { suggestEmailSubjects, draftEmailBody } from "@/lib/ai-comms-api";
import { listSavedSegments, previewAiSegment, saveAiSegment } from "@/lib/ai-segments-api";

const EmailComposer = lazy(() =>
  import("@/components/RichTextEditor").then((m) => ({ default: m.EmailComposer })),
);
const CampaignOpensChart = lazy(() => import("@/pages/admin/CampaignOpensChart"));

const statusColor = {
  Sent: "var(--primary)",
  Scheduled: "#60A5FA",
  Draft: "#71717A",
  "Client draft": "#A78BFA",
};

const STATUS_LABEL = {
  sent: "Sent",
  scheduled: "Scheduled",
  draft: "Draft",
  "client draft": "Client draft",
};

/** Open/click stored as 0–1 rate; clamp display to 100%. */
function formatCampaignRate(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return "—";
  const pct = n <= 1 ? n * 100 : n;
  return `${Math.min(100, Math.round(pct))}%`;
}

/** Unique opens/clicks from Brevo buckets when available (matches delivery detail modal). */
function formatCampaignEngagementSummary(campaign) {
  if (!campaign || typeof campaign !== "object") return "—";
  const opened = campaign.openedRecipients;
  const clicked = campaign.clickedRecipients;
  const openN = Array.isArray(opened) ? opened.length : 0;
  const clickN = Array.isArray(clicked) ? clicked.length : 0;
  if (openN > 0 || clickN > 0) {
    return `${openN} opens · ${clickN} clicks`;
  }
  const openPct = formatCampaignRate(campaign.opens);
  const clickPct = formatCampaignRate(campaign.clicks);
  if (openPct === "—" && clickPct === "—") return "—";
  return `${openPct} / ${clickPct}`;
}

function CampaignStatusPill({ status, sendProvider }) {
  const raw = String(status || "Draft").trim();
  const key = raw.toLowerCase();
  const label = STATUS_LABEL[key] || raw;
  const color = statusColor[raw] || statusColor[label] || "#71717A";
  const showProvider = sendProvider
    && !(key === "client draft" && String(sendProvider).toLowerCase() === "client");

  return (
    <div className="flex flex-col items-start gap-1">
      <span
        className="inline-flex items-center whitespace-nowrap text-[10px] font-mono uppercase tracking-wide px-2 py-1 border rounded-sm leading-none"
        style={{ color, borderColor: `${color}55`, backgroundColor: `${color}12` }}
      >
        {label}
      </span>
      {showProvider ? (
        <span className="text-[9px] font-mono text-[var(--text-tertiary)] uppercase tracking-wide whitespace-nowrap">
          via {sendProvider}
        </span>
      ) : null}
    </div>
  );
}

function DeleteCampaignDialog({ open, onClose, campaign, onConfirm, busy }) {
  const [typed, setTyped] = useState("");
  const confirmWord = "DELETE";

  useEffect(() => {
    if (open) setTyped("");
  }, [open, campaign?.id]);

  if (!open || !campaign) return null;
  const canDelete = typed.trim() === confirmWord;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true">
      <div className="surface w-full max-w-md border border-[#EF4444]/40 p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-heading text-xl text-[#EF4444]">Delete campaign</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              Permanently remove <span className="font-medium text-[var(--text-primary)]">{campaign.name}</span> from this event.
              Sent messages are not recalled. This cannot be undone.
            </p>
          </div>
          <button type="button" className="btn-ghost p-2 cursor-pointer" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <label className="block mt-5 text-xs text-[var(--text-secondary)]">
          Type <span className="font-mono text-[var(--text-primary)]">{confirmWord}</span> to enable delete
          <input
            className="input-dark w-full mt-2 font-mono"
            value={typed}
            onChange={(ev) => setTyped(ev.target.value)}
            placeholder={confirmWord}
            autoComplete="off"
            data-testid="delete-campaign-confirm-input"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost cursor-pointer" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary bg-[#EF4444] hover:bg-[#DC2626] border-[#EF4444] disabled:opacity-40 cursor-pointer"
            disabled={!canDelete || busy}
            onClick={onConfirm}
            data-testid="delete-campaign-confirm-btn"
          >
            {busy ? "Deleting…" : "Delete campaign"}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Compact table icon buttons — do not use .btn-primary/.btn-ghost (min-height 2.75rem breaks 32px cells). */
const COMMS_ICON_ACTION =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--bg-elev)] text-[var(--text-primary)] shadow-sm hover:border-[var(--primary)]/60 hover:text-[var(--primary)] transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none";
const COMMS_ICON_ACTION_SEND =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--primary)]/55 bg-[var(--primary)]/12 text-[var(--primary)] hover:bg-[var(--primary)]/22 hover:border-[var(--primary)] transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none";
const COMMS_ICON_ACTION_DANGER =
  "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-[var(--border-default)] bg-[var(--bg-elev)] text-[var(--text-secondary)] shadow-sm hover:border-[#EF4444]/55 hover:text-[#EF4444] transition-colors cursor-pointer disabled:opacity-40 disabled:pointer-events-none";

function CampaignRowActions({
  campaign,
  canClientDrafts,
  canDispatch,
  sending,
  downloadingZip,
  onPreview,
  onDetail,
  onDownloadEmlZip,
  onDownloadMergeZip,
  downloadingZipMode,
  onSend,
  onDelete,
}) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="flex items-center justify-end gap-1.5 flex-nowrap min-w-0 max-w-full">
      <button
        type="button"
        className={COMMS_ICON_ACTION}
        onClick={onPreview}
        title="Preview recipients"
        aria-label="Preview"
        data-testid={`comms-preview-${campaign.id}`}
      >
        <Eye size={16} strokeWidth={2} />
      </button>
      <button
        type="button"
        className={COMMS_ICON_ACTION}
        onClick={onDetail}
        title="Delivery details"
        aria-label="Details"
        data-testid={`comms-detail-${campaign.id}`}
      >
        <List size={16} strokeWidth={2} />
      </button>
      {canClientDrafts && canDispatch ? (
        <button
          type="button"
          className={COMMS_ICON_ACTION_SEND}
          disabled={sending || downloadingZip}
          onClick={onSend}
          title={sending ? "Sending…" : "Send campaign"}
          aria-label={sending ? "Sending campaign" : "Send campaign"}
          data-testid={`comms-send-${campaign.id}`}
        >
          <Send size={16} strokeWidth={2} />
        </button>
      ) : null}
      {canClientDrafts ? (
        <div className="relative shrink-0">
          <button
            type="button"
            className={COMMS_ICON_ACTION}
            aria-label="More actions"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
          >
            <MoreHorizontal size={16} strokeWidth={2} />
          </button>
          {menuOpen ? (
            <>
              <button
                type="button"
                className="fixed inset-0 z-40 cursor-default"
                aria-label="Close menu"
                onClick={() => setMenuOpen(false)}
              />
              <div className="absolute right-0 top-full z-50 mt-1 min-w-[11rem] surface border border-[var(--border-default)] shadow-lg py-1">
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-elev)] cursor-pointer flex items-center gap-2"
                  disabled={sending || downloadingZip}
                  onClick={() => { setMenuOpen(false); onDownloadEmlZip(); }}
                >
                  <Mail size={12} /> EML drafts ZIP
                </button>
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-elev)] cursor-pointer flex items-center gap-2"
                  disabled={sending || downloadingZip}
                  onClick={() => { setMenuOpen(false); onDownloadMergeZip(); }}
                >
                  <Mail size={12} /> Mail merge kit
                </button>
              </div>
            </>
          ) : null}
        </div>
      ) : null}
      <button
        type="button"
        className={COMMS_ICON_ACTION_DANGER}
        onClick={onDelete}
        title="Delete campaign"
        aria-label="Delete campaign"
        data-testid={`comms-delete-${campaign.id}`}
      >
        <Trash2 size={16} strokeWidth={2} />
      </button>
    </div>
  );
}

const DELIVERY_STATUS_LABEL = {
  sent: "Sent",
  delivered: "Delivered",
  opened: "Opened",
  click: "Clicked",
  soft_bounce: "Soft bounce",
  hard_bounce: "Hard bounce",
  blocked: "Blocked",
  complaint: "Complaint",
  send_failed: "Send failed",
  draft_exported: "Draft exported",
  deferred: "Deferred",
};

const DELIVERY_STATUS_COLOR = {
  sent: "var(--primary)",
  delivered: "#34D399",
  opened: "#60A5FA",
  click: "#A78BFA",
  soft_bounce: "#FBBF24",
  hard_bounce: "#E25D3A",
  blocked: "#71717A",
  complaint: "#EF4444",
  deferred: "#F59E0B",
  send_failed: "#EF4444",
  draft_exported: "#71717A",
};

const METRIC_DEFINITIONS = [
  { key: "sent", label: "Sent", color: "var(--primary)", description: "Total emails sent for this event's campaigns." },
  { key: "deliverability", label: "Deliverability", color: "#34D399", description: "Delivered ÷ sent — share of messages accepted by recipient mail servers." },
  { key: "open_rate", label: "Open rate", color: "#60A5FA", description: "Unique opens ÷ delivered — one open per recipient counts once." },
  { key: "click_rate", label: "Click rate", color: "#A78BFA", description: "Unique clicks ÷ delivered — one click per recipient counts once." },
  { key: "complaint", label: "Complaint", color: "#EF4444", description: "Spam reports from recipients — address is blocked from future sends." },
  { key: "soft_bounce", label: "Soft bounced", color: "#FBBF24", description: "Temporarily undelivered (mailbox full, server busy). May succeed on retry." },
  { key: "deferred", label: "Deferred", color: "#F59E0B", description: "Temporary refusal by the receiving server. Brevo retries for up to ~36 hours." },
  { key: "hard_bounce", label: "Hard bounced", color: "#E25D3A", description: "Permanent undelivery — invalid or non-existent address." },
  { key: "blocked", label: "Blocked", color: "#71717A", description: "Sender or recipient blocked due to spam reputation or repeated hard bounces." },
];

function MetricPill({ label, color }) {
  return (
    <span
      className="inline-flex items-center whitespace-nowrap text-[10px] font-mono uppercase tracking-wide px-2 py-1 border rounded-sm leading-none shrink-0"
      style={{ color, borderColor: `${color}55`, backgroundColor: `${color}12` }}
    >
      {label}
    </span>
  );
}

function DeliveryStatusPill({ status }) {
  const key = String(status || "").trim().toLowerCase();
  const label = DELIVERY_STATUS_LABEL[key] || status;
  const color = DELIVERY_STATUS_COLOR[key] || "#71717A";
  return <MetricPill label={label} color={color} />;
}

function MetricDefinitionsModal({ open, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true" data-testid="comms-metrics-modal">
      <div className="surface w-full max-w-lg border border-[var(--border-default)] shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-[var(--border-default)] flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl">Campaign metrics</h2>
            <p className="text-xs text-[var(--text-tertiary)] mt-2 leading-relaxed">
              Definitions match Maildelivery transactional email reporting. Aggregates on this page sum across all campaigns for this event.
            </p>
          </div>
          <button type="button" className="btn-ghost p-2 cursor-pointer shrink-0" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <ul className="divide-y divide-[var(--border-default)]">
          {METRIC_DEFINITIONS.map((m) => (
            <li key={m.key} className="p-4 flex items-start gap-3">
              <MetricPill label={m.label} color={m.color} />
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed pt-0.5">{m.description}</p>
            </li>
          ))}
        </ul>
        <div className="p-4 border-t border-[var(--border-default)] flex justify-end">
          <button type="button" className="btn-primary cursor-pointer" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// Keep aligned with _VALID_STATUS_SEGMENTS in backend/server.py
const SEGMENT_KIND_OPTIONS = [
  { value: "all", label: "All recipients" },
  { value: "status:Registered", label: "Status: Registered" },
  { value: "status:Paid", label: "Status: Paid" },
  { value: "status:Invited", label: "Status: Invited" },
  { value: "status:Pending", label: "Status: Pending" },
  { value: "status:Pending Approval", label: "Status: Pending Approval" },
  { value: "status:Checked In", label: "Status: Checked In" },
];

const EMPTY_FORM = { name: "", channel: "email", segment: "", segmentKind: "all", subject: "", body: "", replyTo: "", scheduledAt: "", templateId: "" };
const EMPTY_TEMPLATE_FORM = { name: "", description: "", subject: "", body: "", replyTo: "", triggerAction: "manual" };

function formatMaildeliverySyncAt(iso) {
  const raw = String(iso || "").trim();
  if (!raw) return "";
  try {
    return new Date(raw).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return raw.slice(0, 16).replace("T", " ");
  }
}

function readOperatorEmail() {
  try {
    const raw = localStorage.getItem("aivoryems.auth.session");
    const session = raw ? JSON.parse(raw) : null;
    return String(session?.email || "").trim();
  } catch {
    return "";
  }
}

export default function Communications() {
  const { id } = useParams();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [previewState, setPreviewState] = useState(null); // { campaign, loading, data, error }
  const [sendingId, setSendingId] = useState("");
  const [downloadingZipId, setDownloadingZipId] = useState("");
  const [downloadingZipMode, setDownloadingZipMode] = useState("");
  const [deleteCampaignTarget, setDeleteCampaignTarget] = useState(null);
  const [deletingCampaignId, setDeletingCampaignId] = useState("");
  const [detailState, setDetailState] = useState(null); // { campaign, loading, data, error, showFailedOnly }
  const [aiSubjectsBusy, setAiSubjectsBusy] = useState(false);
  const [aiDraftBusy, setAiDraftBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState("");
  // Template state. `templateForm.id` is empty for create, set for edit.
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateForm, setTemplateForm] = useState({ id: "", ...EMPTY_TEMPLATE_FORM });
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [metricsModalOpen, setMetricsModalOpen] = useState(false);
  const [metricsReady, setMetricsReady] = useState(false);
  const [syncingMaildelivery, setSyncingMaildelivery] = useState(false);
  const [maildeliverySyncAt, setMaildeliverySyncAt] = useState("");
  const [aiSegmentPrompt, setAiSegmentPrompt] = useState("");
  const [aiSegmentPreview, setAiSegmentPreview] = useState(null);
  const [aiSegmentBusy, setAiSegmentBusy] = useState(false);
  const [savedAiSegments, setSavedAiSegments] = useState([]);

  const load = useCallback(async () => {
    const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications`);
    if (!res.ok) throw new Error(`Failed to load communications (${res.status})`);
    const data = await res.json();
    setPayload(data);
    if (data.maildeliverySyncAt) setMaildeliverySyncAt(String(data.maildeliverySyncAt));
    return data;
  }, [id]);

  const syncMaildelivery = useCallback(async ({ refresh = true } = {}) => {
    setSyncingMaildelivery(true);
    try {
      const syncRes = await fetch(`${API_BASE}/api/admin/events/${id}/communications/sync-maildelivery`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const syncData = await syncRes.json().catch(() => ({}));
      if (syncRes.ok && syncData.maildeliverySyncAt) {
        setMaildeliverySyncAt(String(syncData.maildeliverySyncAt));
        if (refresh) await load();
        return true;
      }
      if (syncRes.status === 503) {
        return false;
      }
      const detail = syncData?.detail;
      const message = Array.isArray(detail)
        ? detail.map((row) => row?.msg || row?.message).filter(Boolean).join("; ")
        : (detail || syncRes.statusText || "Maildelivery sync failed");
      console.warn("Maildelivery sync skipped:", message);
      return false;
    } catch (err) {
      console.warn("Maildelivery sync skipped:", err?.message || err);
      return false;
    } finally {
      setSyncingMaildelivery(false);
    }
  }, [id, load]);

  const bootstrapComms = useCallback(async () => {
    setLoading(true);
    setMetricsReady(false);
    setError("");
    try {
      await load();
    } catch (err) {
      setError(err?.message || "Failed to load communications");
    } finally {
      setLoading(false);
      setMetricsReady(true);
    }
    void syncMaildelivery({ refresh: true });
  }, [load, syncMaildelivery]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await bootstrapComms();
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapComms]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await listSavedSegments(id);
        if (!cancelled) setSavedAiSegments(rows);
      } catch {
        if (!cancelled) setSavedAiSegments([]);
      }
    })();
    return () => { cancelled = true; };
  }, [id, createOpen]);

  const e = payload?.event;
  const campaigns = Array.isArray(payload?.campaigns) ? payload.campaigns : [];
  const templates = Array.isArray(payload?.templates) ? payload.templates : [];
  const journeySteps = Array.isArray(payload?.journeySteps) ? payload.journeySteps : [];
  const kpis = payload?.kpis || {};
  const showMetrics = metricsReady && !syncingMaildelivery;
  const kpiDisplay = (value, formatter = (v) => String(v)) => {
    if (!showMetrics) return "…";
    return formatter(value);
  };

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setCreateOpen(true);
  };

  // ----- Email templates -----
  const openTemplateCreate = () => {
    setTemplateForm({ id: "", ...EMPTY_TEMPLATE_FORM });
    setTemplateModalOpen(true);
  };

  const submitTemplate = async () => {
    const name = templateForm.name.trim();
    if (!name) {
      toast.error("Template name is required");
      return;
    }
    const body = {
      name,
      description: templateForm.description.trim(),
      subject: templateForm.subject.trim(),
      body: templateForm.body,
      reply_to: templateForm.replyTo.trim(),
      triggerAction: templateForm.triggerAction || "manual",
    };
    setSavingTemplate(true);
    try {
      const isEdit = Boolean(templateForm.id);
      const url = isEdit
        ? `${API_BASE}/api/admin/events/${id}/communications/templates/${templateForm.id}`
        : `${API_BASE}/api/admin/events/${id}/communications/templates`;
      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      toast.success(isEdit ? `Template "${name}" updated` : `Template "${name}" saved`);
      setTemplateModalOpen(false);
      setTemplateForm({ id: "", ...EMPTY_TEMPLATE_FORM });
      await load();
    } catch (err) {
      toast.error(err?.message || "Could not save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const sendTestEmail = async () => {
    const to = testEmail.trim();
    if (!to || !to.includes("@")) {
      toast.error("Enter a valid email address to send the test to");
      return;
    }
    if (!templateForm.subject.trim() && !templateForm.body) {
      toast.error("Add a subject or body before sending a test");
      return;
    }
    setSendingTest(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications/test-send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to,
          subject: templateForm.subject,
          body: templateForm.body,
          reply_to: templateForm.replyTo.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) {
        throw new Error(data?.error || data?.detail || `Test send failed (${res.status})`);
      }
      const provider = data?.provider || "unknown";
      const note = provider === "console"
        ? `Console mode — recorded to outbox, no real email delivered`
        : `Sent via ${provider}`;
      toast.success(`Test sent to ${to} · ${note}`);
    } catch (err) {
      toast.error(err?.message || "Test send failed");
    } finally {
      setSendingTest(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!tpl?.id) return;
    const ok = typeof window !== "undefined" && window.confirm(
      `Delete template "${tpl.name}"? Campaigns that already used it are unaffected (they hold their own copy of the body).`
    );
    if (!ok) return;
    setDeletingTemplateId(tpl.id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications/templates/${tpl.id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      toast.success(`Template "${tpl.name}" deleted`);
      await load();
    } catch (err) {
      toast.error(err?.message || "Could not delete template");
    } finally {
      setDeletingTemplateId("");
    }
  };

  // When the operator picks a template in the New Campaign modal, copy its
  // subject + body into the form. Edits the operator makes after picking are
  // local to the campaign (templates are copy-on-use, not live references).
  // Switching back to "Blank campaign" wipes subject + body so the operator
  // gets a clean slate -- otherwise the previously-applied template content
  // would linger and need manual clearing.
  const fetchFullTemplate = async (templateId) => {
    const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications/templates/${templateId}`);
    if (!res.ok) {
      const fallback = templates.find((t) => t.id === templateId);
      if (fallback) return fallback;
      const payload = await res.json().catch(() => ({}));
      const detail = payload?.detail;
      const message = Array.isArray(detail)
        ? detail.map((row) => row?.msg || row?.message).filter(Boolean).join("; ")
        : (detail || res.statusText);
      throw new Error(message);
    }
    const data = await res.json();
    return data?.template;
  };

  const applyTemplateToCampaignForm = async (templateId) => {
    if (!templateId) {
      setForm((s) => ({ ...s, templateId: "", subject: "", body: "", replyTo: "" }));
      return;
    }
    const tplMeta = templates.find((t) => t.id === templateId);
    if (!tplMeta) return;
    try {
      const tpl = await fetchFullTemplate(templateId);
      if (!tpl) throw new Error("Template not found");
      setForm((s) => ({
        ...s,
        templateId,
        subject: tpl.subject || s.subject,
        body: tpl.bodySource || tpl.body || s.body,
        replyTo: tpl.replyTo || s.replyTo,
      }));
    } catch (err) {
      toast.error(err?.message || "Could not load template");
    }
  };

  const openTemplateEdit = async (tpl) => {
    if (!tpl?.id) return;
    setTemplateModalOpen(true);
    setTemplateForm({
      id: tpl.id || "",
      name: tpl.name || "",
      description: tpl.description || "",
      subject: tpl.subject || "",
      body: "",
      replyTo: tpl.replyTo || "",
      triggerAction: tpl.triggerAction || "manual",
    });
    try {
      const full = await fetchFullTemplate(tpl.id);
      if (!full) throw new Error("Template not found");
      setTemplateForm({
        id: full.id || tpl.id || "",
        name: full.name || "",
        description: full.description || "",
        subject: full.subject || "",
        body: full.bodySource || full.body || "",
        replyTo: full.replyTo || "",
        triggerAction: full.triggerAction || "manual",
      });
    } catch (err) {
      toast.error(err?.message || "Could not load template");
      setTemplateModalOpen(false);
    }
  };

  const openPreview = async (campaign) => {
    setPreviewState({ campaign, loading: true, data: null, error: "" });
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications/campaigns/${campaign.id}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      const data = await res.json();
      setPreviewState({ campaign, loading: false, data, error: "" });
    } catch (err) {
      setPreviewState({ campaign, loading: false, data: null, error: err?.message || "Preview failed" });
    }
  };

  const openCampaignDetail = async (campaign) => {
    if (!campaign?.id) return;
    setDetailState({ campaign, loading: true, data: null, error: "", showFailedOnly: false });
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications/campaigns/${campaign.id}`);
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      const data = await res.json();
      setDetailState({ campaign, loading: false, data, error: "", showFailedOnly: false });
    } catch (err) {
      setDetailState({ campaign, loading: false, data: null, error: err?.message || "Could not load campaign detail" });
    }
  };

  const downloadClientZip = async (campaign, exportMode) => {
    if (!campaign?.id) return;
    const isMerge = exportMode === "mail_merge";
    const ok = typeof window !== "undefined" && window.confirm(
      isMerge
        ? `Download mail merge kit for "${campaign.name}"?\n\n`
          + `ZIP includes:\n`
          + `• 03-campaign-template.oft (Outlook template)\n`
          + `• 03-campaign-template.html (Word mail merge layout)\n`
          + `• 02-recipients.csv (your audience)\n`
          + `• Step-by-step guide for Word → Merge to E-mail → bulk Outbox\n\n`
          + `Requires Microsoft Word + Outlook desktop.`
        : `Download EML drafts ZIP for "${campaign.name}"?\n\n`
          + `One .eml per recipient in the drafts/ folder.\n`
          + `From is NOT set (Outlook uses your account). To, subject, and body are pre-filled for editing.\n`
          + `Double-click each .eml to review and Send individually.`
    );
    if (!ok) return;
    setDownloadingZipId(campaign.id);
    setDownloadingZipMode(exportMode);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/events/${id}/communications/campaigns/${campaign.id}/export-client`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            export_mode: exportMode,
            download_zip: true,
          }),
        },
      );
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      const ct = (res.headers.get("content-type") || "").toLowerCase();
      if (ct.includes("json")) {
        throw new Error("Server returned JSON instead of a ZIP — hard refresh and try again.");
      }
      const blob = await res.blob();
      const sig = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
      if (sig[0] !== 0x50 || sig[1] !== 0x4B) {
        throw new Error("Download is not a valid ZIP file.");
      }
      const cd = res.headers.get("Content-Disposition") || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match?.[1] || `${campaign.name || "campaign"}-${exportMode}.zip`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`, {
        description: isMerge
          ? "Read 01-READ-ME-mail-merge.txt inside the ZIP first."
          : "Unzip → open drafts/ → double-click each .eml in Outlook.",
        duration: 12000,
      });
      setDetailState(null);
      await load();
    } catch (err) {
      toast.error(err?.message || "ZIP download failed");
    } finally {
      setDownloadingZipId("");
      setDownloadingZipMode("");
    }
  };

  const confirmDeleteCampaign = async () => {
    const campaign = deleteCampaignTarget;
    if (!campaign?.id) return;
    setDeletingCampaignId(campaign.id);
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/events/${id}/communications/campaigns/${campaign.id}`,
        { method: "DELETE" },
      );
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      toast.success(`Campaign "${campaign.name}" deleted`);
      setDeleteCampaignTarget(null);
      setDetailState(null);
      await load();
    } catch (err) {
      toast.error(err?.message || "Could not delete campaign");
    } finally {
      setDeletingCampaignId("");
    }
  };

  const runRecipientAction = async (campaign, recipient, actionId) => {
    if (!campaign?.id || !recipient) return;
    const attendeeId = recipient.attendeeId;
    const email = recipient.email;
    const key = `${campaign.id}:${email}:${actionId}`;
    setActionBusy(key);
    try {
      if (actionId === "reattempt") {
        const res = await fetch(
          `${API_BASE}/api/admin/events/${id}/communications/campaigns/${campaign.id}/resend-recipient`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ email }),
          },
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Resend failed");
        toast.success(`Re-sent to ${email}`);
      } else if (actionId === "edit_email") {
        if (!attendeeId) {
          toast.error("No attendee linked — update the email on the attendee record manually");
          return;
        }
        const next = typeof window !== "undefined" ? window.prompt("New email address", email) : null;
        if (!next || !next.includes("@")) return;
        const res = await fetch(`${API_BASE}/api/events/${id}/attendees/${attendeeId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: next.trim() }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Update failed");
        toast.success(`Email updated to ${next.trim()}`);
      } else if (actionId === "remove") {
        if (!attendeeId) {
          toast.error("No attendee linked for this address");
          return;
        }
        const okDel = typeof window !== "undefined" && window.confirm(`Remove attendee ${email} from this event?`);
        if (!okDel) return;
        const res = await fetch(`${API_BASE}/api/events/${id}/attendees/${attendeeId}`, { method: "DELETE" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.detail || "Delete failed");
        toast.success(`Removed ${email}`);
      }
      await openCampaignDetail(campaign);
      await load();
    } catch (err) {
      toast.error(err?.message || "Action failed");
    } finally {
      setActionBusy("");
    }
  };

  const sendCampaign = async (campaign) => {
    if (!campaign?.id) return;
    if (String(campaign.channel || "email").toLowerCase() === "sms") {
      toast.error("SMS not configured", {
        description: "Connect Brevo SMS (or your provider) before sending SMS campaigns.",
      });
      return;
    }
    const ok = typeof window !== "undefined" && window.confirm(
      `Dispatch "${campaign.name}" now? This will email every recipient resolved by the campaign's audience filter.`
    );
    if (!ok) return;
    setSendingId(campaign.id);
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications/campaigns/${campaign.id}/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      const data = await res.json();
      const failedSuffix = data.failed ? ` · ${data.failed} failed` : "";
      if (data.piped) {
        toast.success(`SMS piped for ${data.delivered} recipients`, {
          description: "Provider not linked — campaign marked sent in EMS only.",
        });
      } else {
        toast.success(`Sent ${data.delivered} of ${data.delivered + (data.failed || 0)} via ${data.provider}${failedSuffix}`);
      }
      setPreviewState(null);
      await load();
    } catch (err) {
      toast.error(err?.message || "Send failed");
    } finally {
      setSendingId("");
    }
  };

  const submitCreate = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error("Campaign name is required");
      return;
    }
    let scheduledIso = null;
    if (form.scheduledAt) {
      const d = new Date(form.scheduledAt);
      if (Number.isNaN(d.getTime())) {
        toast.error("Schedule must be a valid date/time");
        return;
      }
      scheduledIso = d.toISOString();
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/events/${id}/communications/campaigns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          channel: form.channel || "email",
          segment: form.segment.trim(),
          segment_kind: form.segmentKind,
          subject: form.subject.trim(),
          body: form.body.trim(),
          reply_to: form.replyTo.trim(),
          scheduled_at: scheduledIso,
        }),
      });
      if (!res.ok) {
        const detail = (await res.json().catch(() => ({}))).detail || res.statusText;
        throw new Error(detail);
      }
      const data = await res.json();
      toast.success(`Campaign "${data.campaign?.name || name}" created (${data.campaign?.status || "Draft"})`);
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      toast.error(err?.message || "Could not create campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-[1500px] mx-auto min-w-0 w-full" data-testid="comms-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-overline">{e?.title || id} · communications</div>
          <h1 className="font-heading text-4xl mt-2 tracking-tight font-medium">Comms & journeys</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-2">Reusable email templates and segmented campaigns. Templates and KPIs below are scoped to this event only.</p>
        </div>
        <button className="btn-primary" onClick={openCreate} data-testid="comms-new-campaign">
          <Plus size={14} /> New campaign
        </button>
      </div>

      {/* KPIs — synced from Maildelivery on page load (event creation → today) */}
      <div className="mt-10">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <h2 className="text-overline">Event metrics</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="text-xs font-mono text-[var(--text-tertiary)]"
              data-testid="comms-maildelivery-sync-at"
            >
              {syncingMaildelivery
                ? "Syncing from Maildelivery…"
                : maildeliverySyncAt
                  ? `Synced at ${formatMaildeliverySyncAt(maildeliverySyncAt)}`
                  : showMetrics
                    ? "Maildelivery metrics (sync optional)"
                    : ""}
            </span>
            <button
              type="button"
              className="btn-ghost px-2 py-1.5 text-xs inline-flex items-center gap-1.5"
              disabled={syncingMaildelivery}
              onClick={() => syncMaildelivery({ refresh: true })}
              data-testid="comms-maildelivery-sync-btn"
              title="Pull delivery events from Maildelivery"
            >
              <RefreshCw size={14} className={syncingMaildelivery ? "animate-spin" : ""} />
              Sync
            </button>
            <button
              type="button"
              className="btn-ghost px-2.5 py-1.5 cursor-pointer text-[var(--text-tertiary)] hover:text-[var(--text-primary)] inline-flex items-center gap-1.5 text-xs"
              onClick={() => setMetricsModalOpen(true)}
              aria-label="Metric definitions"
              title="What do these metrics mean?"
              data-testid="comms-metrics-help"
            >
              <HelpCircle size={16} />
              <span>What the metrics mean</span>
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3">
          <KPICard icon={Send} k="Emails sent" v={kpiDisplay(kpis.totalSent, (v) => (v || 0).toLocaleString())} />
          <KPICard icon={Eye} k="Avg open rate" v={kpiDisplay(kpis.openRate, (v) => `${v || 0}%`)} />
          <KPICard icon={MousePointerClick} k="Avg click rate" v={kpiDisplay(kpis.clickRate, (v) => `${v || 0}%`)} />
          <KPICard icon={Users} k="Recipients" v={kpiDisplay(kpis.uniqueRecipients, (v) => (v || 0).toLocaleString())} />
          <KPICard icon={List} k="Delivered" v={kpiDisplay(kpis.delivered, (v) => (v || 0).toLocaleString())} />
          <KPICard
            icon={MailX}
            k="Bounced"
            v={kpiDisplay(kpis, (row) => ((row?.bouncedSoft || 0) + (row?.bouncedHard || 0)).toLocaleString())}
          />
          <KPICard icon={ShieldAlert} k="Blocked" v={kpiDisplay(kpis.blocked, (v) => (v || 0).toLocaleString())} />
          <KPICard icon={AlertTriangle} k="Complaints" v={kpiDisplay(kpis.complaints, (v) => (v || 0).toLocaleString())} />
        </div>
      </div>

      {/* Email templates + Campaigns split */}
      <div className="mt-12 grid grid-cols-12 gap-6 min-w-0">
        <div className="col-span-12 lg:col-span-5 min-w-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-2xl flex items-center gap-2">
              <FileText size={20} className="text-[var(--text-secondary)]" /> Email templates
            </h2>
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={openTemplateCreate}
              data-testid="comms-new-template"
            >
              <Plus size={12} /> New template
            </button>
          </div>
          <div className="surface divide-y divide-[var(--border-default)]">
            {templates.map((tpl) => (
              <div key={tpl.id} className="p-4 flex items-start justify-between gap-3 hover:bg-[var(--bg-elev)]/50 transition">
                <button
                  type="button"
                  className="flex-1 text-left min-w-0"
                  onClick={() => openTemplateEdit(tpl)}
                  data-testid={`comms-template-${tpl.id}`}
                  title="Click to edit"
                >
                  <div className="text-sm font-medium truncate flex items-center gap-2">
                    <span className="truncate">{tpl.name}</span>
                    {tpl.triggerAction === "on_approval" ? (
                      <span
                        className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-[#34D399]/40 bg-[#34D399]/10 text-[#34D399]"
                        title="Auto-sent when an attendee is approved"
                      >
                        Auto: on approval
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs font-mono text-[var(--text-tertiary)] mt-1 truncate">
                    {tpl.description || tpl.subject || "—"}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    className="btn-ghost p-1.5"
                    onClick={() => openTemplateEdit(tpl)}
                    aria-label="Edit"
                    title="Edit"
                  >
                    <Edit2 size={13} />
                  </button>
                  <button
                    type="button"
                    className="btn-ghost p-1.5 text-[var(--error)]"
                    onClick={() => deleteTemplate(tpl)}
                    disabled={deletingTemplateId === tpl.id}
                    aria-label="Delete"
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            ))}
            {!loading && !error && templates.length === 0 ? (
              <div className="p-6 text-center">
                <FileText size={28} strokeWidth={1.2} className="mx-auto text-[var(--text-tertiary)]" />
                <div className="text-sm text-[var(--text-secondary)] mt-3">No templates yet for this event.</div>
                <button
                  type="button"
                  className="btn-secondary text-xs mt-4 justify-center"
                  onClick={openTemplateCreate}
                  data-testid="comms-template-empty-cta"
                >
                  <Plus size={12} /> Create your first template
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 min-w-0">
          <h2 className="font-heading text-2xl mb-4">Campaigns</h2>
          <div className="surface overflow-x-auto min-w-0 max-w-full overscroll-x-contain">
            <table className="w-full text-sm min-w-[720px]">
                <thead className="border-b border-[var(--border-default)] text-[var(--text-tertiary)] font-mono text-[10px] uppercase tracking-widest bg-[var(--bg-elev)]/40">
                  <tr>
                    <th className="px-4 py-3 text-left min-w-0 max-w-[220px]">Campaign</th>
                    <th className="px-4 py-3 text-left w-[120px]">Schedule</th>
                    <th className="px-4 py-3 text-right w-[72px]">Open</th>
                    <th className="px-4 py-3 text-right w-[72px]">Click</th>
                    <th className="px-4 py-3 text-left w-[120px]">Status</th>
                    <th className="px-4 py-3 text-right w-[168px]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => {
                    const statusLower = String(c.status || "").toLowerCase();
                    const sent = statusLower === "sent";
                    const clientDraft = statusLower === "client draft";
                    const canClientDrafts = !sent;
                    const canDispatch = !sent && !clientDraft;
                    const sending = sendingId === c.id;
                    const downloadingZip = downloadingZipId === c.id;
                    const issues = (c.bouncedSoft || 0) + (c.bouncedHard || 0) + (c.blocked || 0) + (c.complaints || 0);
                    return (
                      <tr key={c.id} className="border-b border-[var(--border-default)] last:border-b-0 hover:bg-[var(--bg-elev)]/60 align-middle">
                        <td className="px-4 py-3 min-w-0 max-w-[220px]">
                          <button type="button" className="text-left cursor-pointer w-full min-w-0" onClick={() => openCampaignDetail(c)}>
                            <div className="font-medium hover:text-[var(--primary)] truncate" title={c.name}>{c.name}</div>
                            <div className="text-xs text-[var(--text-tertiary)] font-mono mt-1 truncate" title={c.segment}>{c.segment}</div>
                            {issues > 0 ? (
                              <div className="text-[10px] font-mono text-[#E25D3A] mt-1">{issues} delivery issue{issues === 1 ? "" : "s"}</div>
                            ) : null}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-[var(--text-secondary)] min-w-0">
                          <span className="inline-flex items-center gap-1.5 min-w-0 max-w-[120px]">
                            <Calendar size={11} className="shrink-0 opacity-70" />
                            <span className="truncate" title={c.scheduled}>{c.scheduled}</span>
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums">{formatCampaignRate(c.opens)}</td>
                        <td className="px-4 py-3 text-right font-mono tabular-nums">{formatCampaignRate(c.clicks)}</td>
                        <td className="px-4 py-3 min-w-0 w-[108px]">
                          <CampaignStatusPill status={c.status} sendProvider={c.sendProvider} />
                        </td>
                        <td className="px-4 py-3 min-w-0">
                          <CampaignRowActions
                            campaign={c}
                            canClientDrafts={canClientDrafts}
                            canDispatch={canDispatch}
                            sending={sending}
                            downloadingZip={downloadingZip}
                            onPreview={() => openPreview(c)}
                            onDetail={() => openCampaignDetail(c)}
                            onDownloadEmlZip={() => downloadClientZip(c, "eml_drafts")}
                            onDownloadMergeZip={() => downloadClientZip(c, "mail_merge")}
                            downloadingZipMode={downloadingZipId === c.id ? downloadingZipMode : ""}
                            onSend={() => sendCampaign(c)}
                            onDelete={() => setDeleteCampaignTarget(c)}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
            </table>
            {!loading && !error && !campaigns.length && (
              <div className="p-6 text-sm text-[var(--text-secondary)] text-center">No campaigns available.</div>
            )}
          </div>

          {/* Journey teaser */}
          <h2 className="font-heading text-2xl mt-12 mb-4">Journey · Last 7 days</h2>
          <div className="surface p-6">
            <div className="flex items-center justify-between text-xs font-mono text-[var(--text-tertiary)] uppercase tracking-widest">
              <span>Trigger: Order confirmed</span>
              <span>Recipients: {(kpis.uniqueRecipients || 0).toLocaleString()}</span>
            </div>
            <div className="mt-6 flex items-center gap-3 overflow-x-auto pb-2">
              {journeySteps.map((step, i) => (
                <div key={step} className="flex items-center gap-3 shrink-0">
                  <div className="surface px-4 py-3 min-w-[140px]">
                    <div className="text-[10px] font-mono text-[var(--text-tertiary)] uppercase tracking-widest">Step {i + 1}</div>
                    <div className="text-sm font-medium mt-1">{typeof step === "string" ? step : step?.name || "Journey step"}</div>
                  </div>
                  {i < journeySteps.length - 1 && <span className="text-[var(--text-tertiary)] font-mono">→</span>}
                </div>
              ))}
            </div>
            {!loading && !error && journeySteps.length === 0 ? <div className="mt-4 text-sm text-[var(--text-secondary)]">No journey steps available.</div> : null}
          </div>
        </div>
      </div>
      {error && <div className="mt-6 text-sm text-[var(--error)]">{error}</div>}

      {previewState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" data-testid="comms-preview-modal">
          <div className="surface max-w-xl w-full border border-[var(--border-default)] shadow-xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-[var(--border-default)] flex justify-between items-start gap-4 shrink-0">
              <div>
                <div className="font-heading text-xl">Preview · {previewState.campaign.name}</div>
                <p className="text-xs text-[var(--text-tertiary)] mt-2 leading-relaxed">
                  Recipients are resolved by the campaign's audience filter at this moment. Send uses the same list.
                </p>
              </div>
              <button type="button" className="btn-ghost p-2 shrink-0" aria-label="Close" onClick={() => setPreviewState(null)}>
                <X size={18} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {previewState.loading && <div className="text-sm text-[var(--text-tertiary)]">Resolving recipients…</div>}
              {previewState.error && <div className="text-sm text-[var(--error)]">{previewState.error}</div>}
              {previewState.data && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                    <div className="surface border border-[var(--border-default)] p-4">
                      <div className="text-overline">Recipients</div>
                      <div className="font-mono text-2xl mt-2" data-testid="comms-preview-count">{previewState.data.recipientCount.toLocaleString()}</div>
                    </div>
                    <div className="surface border border-[var(--border-default)] p-4">
                      <div className="text-overline">Audience filter</div>
                      <div className="font-mono text-xs mt-2 break-all leading-relaxed">{previewState.data.segmentLabel}</div>
                    </div>
                  </div>
                  <div className="text-overline mb-3">Sample (first 5)</div>
                  <div className="surface divide-y divide-[var(--border-default)] max-h-[200px] overflow-y-auto border border-[var(--border-default)]">
                    {previewState.data.sample.length === 0 && (
                      <div className="p-4 text-xs text-[var(--text-tertiary)]">No recipients match this segment.</div>
                    )}
                    {previewState.data.sample.map((s) => (
                      <div key={s.id || s.email} className="p-3 text-xs font-mono">
                        <div className="text-[var(--text-primary)]">{s.email}</div>
                        <div className="text-[var(--text-tertiary)] mt-0.5">{s.name || "—"}{s.company ? ` · ${s.company}` : ""}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 text-[10px] font-mono uppercase tracking-widest" style={{ color: previewState.data.providerReady ? "var(--primary)" : "#E25D3A" }}>
                    Provider: {previewState.data.provider} {previewState.data.providerReady ? "· ready" : "· not ready"}
                  </div>
                  {!previewState.data.providerReady && (
                    <div className="text-[10px] font-mono text-[var(--error)] mt-1">{previewState.data.providerReason}</div>
                  )}
                </>
              )}
            </div>
            <div className="p-6 border-t border-[var(--border-default)] flex justify-end gap-2 flex-wrap shrink-0">
              <button type="button" className="btn-ghost" onClick={() => setPreviewState(null)}>
                Close
              </button>
              {previewState.data && previewState.data.recipientCount > 0
                && String(previewState.campaign.status || "").toLowerCase() !== "sent" && (
                <>
                  <button
                    type="button"
                    className="btn-ghost"
                    disabled={downloadingZipId === previewState.campaign.id}
                    onClick={() => downloadClientZip(previewState.campaign, "eml_drafts")}
                    data-testid="comms-preview-eml-zip"
                  >
                    {downloadingZipId === previewState.campaign.id && downloadingZipMode === "eml_drafts" ? "…" : "EML ZIP"}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    disabled={downloadingZipId === previewState.campaign.id || sendingId === previewState.campaign.id}
                    onClick={() => downloadClientZip(previewState.campaign, "mail_merge")}
                    data-testid="comms-preview-merge-zip"
                  >
                    {downloadingZipId === previewState.campaign.id && downloadingZipMode === "mail_merge" ? "…" : "Merge kit"}
                  </button>
                  {previewState.data.providerReady
                    && String(previewState.campaign.status || "").toLowerCase() !== "client draft" && (
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={sendingId === previewState.campaign.id}
                      onClick={() => sendCampaign(previewState.campaign)}
                      data-testid="comms-preview-send"
                    >
                      {sendingId === previewState.campaign.id ? "Sending…" : `Send to ${previewState.data.recipientCount}`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <DeleteCampaignDialog
        open={Boolean(deleteCampaignTarget)}
        campaign={deleteCampaignTarget}
        onClose={() => setDeleteCampaignTarget(null)}
        onConfirm={confirmDeleteCampaign}
        busy={Boolean(deletingCampaignId)}
      />

      {detailState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" data-testid="comms-detail-modal">
          <div className="surface max-w-4xl w-full border border-[var(--border-default)] shadow-xl max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-[var(--border-default)] flex justify-between items-start gap-4 shrink-0">
              <div>
                <div className="font-heading text-xl">Delivery · {detailState.campaign.name}</div>
                <p className="text-xs text-[var(--text-tertiary)] mt-2 leading-relaxed">
                  Per-recipient timeline from Maildelivery (delivered, open, click, bounces, blocked, complaint).
                </p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  className="btn-ghost p-2 cursor-pointer text-[var(--text-tertiary)]"
                  onClick={() => setMetricsModalOpen(true)}
                  aria-label="Metric definitions"
                  title="What do these metrics mean?"
                >
                  <HelpCircle size={16} />
                </button>
                <button type="button" className="btn-ghost p-2" aria-label="Close" onClick={() => setDetailState(null)}>
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {detailState.loading && <div className="text-sm text-[var(--text-tertiary)]">Loading delivery data…</div>}
              {detailState.error && <div className="text-sm text-[var(--error)]">{detailState.error}</div>}
              {detailState.data && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                    <StatCard label="Sent" value={detailState.data.campaign?.sent || 0} />
                    <StatCard label="Delivered" value={detailState.data.campaign?.deliveredCount || 0} />
                    <StatCard label="Issues" value={detailState.data.undeliveredCount || 0} accent="#E25D3A" />
                    <StatCard
                      label="Opens / clicks"
                      value={formatCampaignEngagementSummary(detailState.data.campaign)}
                      small
                    />
                  </div>
                  <div className="surface border border-[var(--border-default)] p-4 mb-6">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-overline">Opens by day</div>
                      <span className="text-[10px] font-mono text-[var(--text-tertiary)]">From webhook event timestamps</span>
                    </div>
                    <Suspense fallback={<div className="text-sm text-[var(--text-tertiary)] py-8 text-center">Loading chart…</div>}>
                      <CampaignOpensChart series={detailState.data.dailyOpens} loading={detailState.loading} />
                    </Suspense>
                  </div>
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div className="text-overline">Recipients</div>
                    <label className="flex items-center gap-2 text-xs font-mono text-[var(--text-secondary)] cursor-pointer">
                      <input
                        type="checkbox"
                        className="accent-[var(--primary)]"
                        checked={Boolean(detailState.showFailedOnly)}
                        onChange={(ev) => setDetailState((s) => ({ ...s, showFailedOnly: ev.target.checked }))}
                      />
                      Show delivery issues only
                    </label>
                  </div>
                  <div className="surface divide-y divide-[var(--border-default)] max-h-[280px] overflow-y-auto border border-[var(--border-default)]">
                    {(detailState.showFailedOnly
                      ? (detailState.data.undelivered || [])
                      : (detailState.data.recipients || [])
                    ).length === 0 && (
                      <div className="p-4 text-xs text-[var(--text-tertiary)]">
                        {detailState.showFailedOnly
                          ? "No delivery issues recorded yet."
                          : "No per-recipient log yet — send the campaign or wait for Maildelivery sync on the Comms page."}
                      </div>
                    )}
                    {(detailState.showFailedOnly
                      ? detailState.data.undelivered
                      : detailState.data.recipients
                    )?.map((r) => (
                      <div key={r.email} className="p-4 text-xs">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-mono text-[var(--text-primary)] truncate">{r.email}</div>
                            <div className="mt-2">
                              <DeliveryStatusPill status={r.status} />
                            </div>
                          </div>
                          {r.undelivered && Array.isArray(r.recommendedActions) ? (
                            <div className="flex flex-wrap gap-1.5 justify-end shrink-0">
                              {r.recommendedActions.map((act) => (
                                <button
                                  key={act.id}
                                  type="button"
                                  className="btn-ghost text-[9px] uppercase tracking-widest px-2.5 py-1 border border-[var(--border-default)]"
                                  disabled={actionBusy === `${detailState.campaign.id}:${r.email}:${act.id}`}
                                  onClick={() => runRecipientAction(detailState.campaign, r, act.id)}
                                >
                                  {act.label}
                                </button>
                              ))}
                            </div>
                          ) : null}
                        </div>
                        {Array.isArray(r.events) && r.events.length > 0 ? (
                          <div className="mt-3 pl-3 border-l border-[var(--border-default)] space-y-1.5">
                            {r.events.map((ev, i) => (
                              <div key={`${ev.type}-${ev.ts}-${i}`} className="font-mono text-[10px] text-[var(--text-tertiary)] flex items-center gap-2 flex-wrap">
                                <DeliveryStatusPill status={ev.type} />
                                {ev.ts ? <span>{String(ev.ts).slice(0, 19).replace("T", " ")}</span> : null}
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="p-6 border-t border-[var(--border-default)] flex justify-end gap-2 flex-wrap shrink-0">
              <button
                type="button"
                className="btn-ghost"
                onClick={() => detailState.campaign && openCampaignDetail(detailState.campaign)}
              >
                <RefreshCw size={12} className="inline mr-1" /> Refresh
              </button>
              <button type="button" className="btn-ghost" onClick={() => setDetailState(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" data-testid="comms-create-modal">
          <div className="surface max-w-lg w-full p-6 border border-[var(--border-default)] shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-heading text-xl">New campaign</div>
                <p className="text-xs text-[var(--text-tertiary)] mt-2">
                  Save as a draft or schedule it. After saving, use Preview to confirm the recipient list, then Send to dispatch via the configured email provider.
                </p>
              </div>
              <button type="button" className="btn-ghost p-2" aria-label="Close" onClick={() => setCreateOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="mt-4 grid gap-3">
              {templates.length > 0 && (
                <label className="block">
                  <span className="text-overline">Start from template (optional)</span>
                  <select
                    className="input-dark mt-1 w-full"
                    value={form.templateId}
                    onChange={(ev) => applyTemplateToCampaignForm(ev.target.value)}
                    data-testid="comms-form-template-picker"
                  >
                    <option value="">— Blank campaign —</option>
                    {templates.map((tpl) => (
                      <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
                    ))}
                  </select>
                  <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
                    Picks subject + body from the template. Edits made here only affect this campaign; the template is left untouched.
                  </span>
                </label>
              )}
              <label className="block">
                <span className="text-overline">Name</span>
                <input
                  className="input-dark mt-1 w-full"
                  placeholder="e.g. Early Bird — Final 48h"
                  value={form.name}
                  onChange={(ev) => setForm((s) => ({ ...s, name: ev.target.value }))}
                  data-testid="comms-form-name"
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="text-overline">Channel</span>
                <select
                  className="input-dark mt-1 w-full"
                  value={form.channel}
                  onChange={(ev) => setForm((s) => ({ ...s, channel: ev.target.value }))}
                  data-testid="comms-form-channel"
                >
                  <option value="email">Email</option>
                  <option value="sms">SMS (requires provider setup)</option>
                </select>
              </label>
              <label className="block">
                <span className="text-overline">Audience filter</span>
                <select
                  className="input-dark mt-1 w-full"
                  value={form.segmentKind}
                  onChange={(ev) => setForm((s) => ({ ...s, segmentKind: ev.target.value }))}
                  data-testid="comms-form-segment-kind"
                >
                  {SEGMENT_KIND_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                  {savedAiSegments.map((seg) => (
                    <option key={seg.id} value={`ai:${seg.id}`}>
                      AI: {seg.name || seg.prompt?.slice(0, 40) || seg.id}
                    </option>
                  ))}
                </select>
                <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
                  Recipients are resolved from this filter at preview and send time.
                </span>
              </label>
              <div className="rounded-lg border border-[var(--border-default)] p-3 space-y-2">
                <div className="text-overline flex items-center justify-between gap-2">
                  <span>AI audience builder</span>
                  <span className="text-[10px] font-mono text-[var(--text-tertiary)]">Studio+</span>
                </div>
                <textarea
                  className="input-dark w-full text-sm min-h-[72px]"
                  placeholder='e.g. "VPs in Singapore who have not checked in"'
                  value={aiSegmentPrompt}
                  onChange={(ev) => setAiSegmentPrompt(ev.target.value)}
                  data-testid="comms-ai-segment-prompt"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-secondary text-xs"
                    disabled={aiSegmentBusy || !aiSegmentPrompt.trim()}
                    onClick={async () => {
                      setAiSegmentBusy(true);
                      try {
                        const body = await previewAiSegment({ prompt: aiSegmentPrompt.trim(), eventId: id });
                        setAiSegmentPreview(body.preview);
                        toast.success("Segment preview ready", {
                          description: `${body.preview?.count ?? 0} recipients match`,
                        });
                      } catch (err) {
                        toast.error(err?.message || "Could not preview segment");
                      } finally {
                        setAiSegmentBusy(false);
                      }
                    }}
                  >
                    Preview count
                  </button>
                  <button
                    type="button"
                    className="btn-primary text-xs"
                    disabled={aiSegmentBusy || !aiSegmentPrompt.trim()}
                    onClick={async () => {
                      setAiSegmentBusy(true);
                      try {
                        const body = await saveAiSegment({
                          prompt: aiSegmentPrompt.trim(),
                          eventId: id,
                          name: aiSegmentPrompt.trim().slice(0, 60),
                        });
                        const entry = body.segment;
                        setSavedAiSegments((prev) => [...prev, entry]);
                        setForm((s) => ({
                          ...s,
                          segmentKind: `ai:${entry.id}`,
                          segment: entry.name || aiSegmentPrompt.trim().slice(0, 60),
                        }));
                        toast.success("Saved AI segment", {
                          description: `${entry.previewCount ?? 0} recipients`,
                        });
                      } catch (err) {
                        toast.error(err?.message || "Could not save segment");
                      } finally {
                        setAiSegmentBusy(false);
                      }
                    }}
                  >
                    Save & use
                  </button>
                </div>
                {aiSegmentPreview ? (
                  <div className="text-[10px] font-mono text-[var(--text-tertiary)]">
                    Preview: {aiSegmentPreview.count ?? 0} recipients
                  </div>
                ) : null}
              </div>
              <label className="block">
                <span className="text-overline">Segment label (display only)</span>
                <input
                  className="input-dark mt-1 w-full"
                  placeholder="Optional — defaults to the audience filter name"
                  value={form.segment}
                  onChange={(ev) => setForm((s) => ({ ...s, segment: ev.target.value }))}
                  data-testid="comms-form-segment"
                />
              </label>
              <label className="block">
                <span className="text-overline flex items-center justify-between gap-2">
                  Subject line
                  <button
                    type="button"
                    className="text-[10px] font-mono uppercase tracking-wide text-[var(--primary)] inline-flex items-center gap-1 disabled:opacity-40"
                    disabled={aiSubjectsBusy}
                    onClick={async () => {
                      setAiSubjectsBusy(true);
                      try {
                        const subjects = await suggestEmailSubjects({
                          message: form.name || form.subject || "Event update",
                          eventId: id,
                        });
                        if (subjects[0]) setForm((s) => ({ ...s, subject: subjects[0] }));
                        toast.success("Subject suggestions ready", {
                          description: subjects.slice(0, 3).join(" · "),
                        });
                      } catch (err) {
                        toast.error(err?.message || "Could not suggest subjects");
                      } finally {
                        setAiSubjectsBusy(false);
                      }
                    }}
                    data-testid="comms-ai-subjects"
                  >
                    <Sparkles size={12} /> AI subjects
                  </button>
                </span>
                <input
                  className="input-dark mt-1 w-full"
                  placeholder="Subject"
                  value={form.subject}
                  onChange={(ev) => setForm((s) => ({ ...s, subject: ev.target.value }))}
                  data-testid="comms-form-subject"
                />
              </label>
              <label className="block">
                <span className="text-overline">Reply-To (optional)</span>
                <input
                  type="email"
                  className="input-dark mt-1 w-full"
                  placeholder="events@yourdomain.com"
                  value={form.replyTo}
                  onChange={(ev) => setForm((s) => ({ ...s, replyTo: ev.target.value }))}
                  data-testid="comms-form-reply-to"
                />
                <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
                  Recipients hitting "Reply" reach this inbox. Leave blank to fall back to the
                  configured sender (SMTP_FROM).
                </span>
              </label>
              <label className="block">
                <span className="text-overline flex items-center justify-between gap-2">
                  Body
                  <button
                    type="button"
                    className="text-[10px] font-mono uppercase tracking-wide text-[var(--primary)] inline-flex items-center gap-1 disabled:opacity-40"
                    disabled={aiDraftBusy}
                    onClick={async () => {
                      setAiDraftBusy(true);
                      try {
                        const body = await draftEmailBody({
                          message: form.name || form.subject || "Event invitation",
                          eventId: id,
                        });
                        if (body) setForm((s) => ({ ...s, body }));
                        toast.success("Draft generated", { description: "Review and edit before sending." });
                      } catch (err) {
                        toast.error(err?.message || "Could not generate draft");
                      } finally {
                        setAiDraftBusy(false);
                      }
                    }}
                    data-testid="comms-ai-draft"
                  >
                    <Sparkles size={12} /> AI draft
                  </button>
                </span>
                <textarea
                  className="input-dark mt-1 w-full min-h-[160px] font-mono text-xs"
                  placeholder="Plain text or HTML. Tokens substituted at send time: {{firstName}}, {{name}}, {{email}}, {{company}}, {{eventTitle}}, {{eventDate}}, {{eventVenue}}."
                  value={form.body}
                  onChange={(ev) => setForm((s) => ({ ...s, body: ev.target.value }))}
                  data-testid="comms-form-body"
                />
              </label>
              <label className="block">
                <span className="text-overline">Schedule (optional)</span>
                <input
                  type="datetime-local"
                  className="input-dark mt-1 w-full"
                  value={form.scheduledAt}
                  onChange={(ev) => setForm((s) => ({ ...s, scheduledAt: ev.target.value }))}
                  data-testid="comms-form-scheduled"
                />
                <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
                  {form.scheduledAt ? "Saves as Scheduled." : "Leave empty to save as Draft."}
                </span>
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setCreateOpen(false)} disabled={saving}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={saving || !form.name.trim()}
                onClick={submitCreate}
                data-testid="comms-form-submit"
              >
                {saving ? "Saving…" : "Create campaign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {templateModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" role="dialog" data-testid="comms-template-modal">
          <div className="surface max-w-4xl w-full p-6 border border-[var(--border-default)] shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start gap-4">
              <div>
                <div className="font-heading text-xl">{templateForm.id ? "Edit template" : "New template"}</div>
                <p className="text-xs text-[var(--text-tertiary)] mt-2 leading-relaxed">
                  Save reusable invite or outreach content for this event. Pick a saved template when creating a campaign to pre-fill subject + body. Existing campaigns aren't affected by edits to a template (they hold their own copy).
                </p>
              </div>
              <button type="button" className="btn-ghost p-2" aria-label="Close" onClick={() => setTemplateModalOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="mt-4 grid gap-3">
              <label className="block">
                <span className="text-overline">Name</span>
                <input
                  className="input-dark mt-1 w-full"
                  placeholder="e.g. Groundbreaking ceremony invite"
                  value={templateForm.name}
                  onChange={(ev) => setTemplateForm((s) => ({ ...s, name: ev.target.value }))}
                  data-testid="comms-template-form-name"
                  autoFocus
                />
              </label>
              <label className="block">
                <span className="text-overline">Description (one-liner shown in the list)</span>
                <input
                  className="input-dark mt-1 w-full"
                  placeholder="Optional — what this template is for"
                  value={templateForm.description}
                  onChange={(ev) => setTemplateForm((s) => ({ ...s, description: ev.target.value }))}
                  data-testid="comms-template-form-description"
                />
              </label>
              <label className="block">
                <span className="text-overline">Subject line</span>
                <input
                  className="input-dark mt-1 w-full"
                  placeholder="e.g. You're invited: {{eventTitle}}"
                  value={templateForm.subject}
                  onChange={(ev) => setTemplateForm((s) => ({ ...s, subject: ev.target.value }))}
                  data-testid="comms-template-form-subject"
                />
              </label>
              <label className="block">
                <span className="text-overline">Reply-To (optional)</span>
                <input
                  type="email"
                  className="input-dark mt-1 w-full"
                  placeholder="events@yourdomain.com"
                  value={templateForm.replyTo}
                  onChange={(ev) => setTemplateForm((s) => ({ ...s, replyTo: ev.target.value }))}
                  data-testid="comms-template-form-reply-to"
                />
                <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
                  Carried into campaigns when this template is picked. Recipients hitting
                  "Reply" reach this inbox. Leave blank to fall back to SMTP_FROM.
                </span>
              </label>
              <label className="block">
                <span className="text-overline">When to send</span>
                <select
                  className="input-dark mt-1 w-full"
                  value={templateForm.triggerAction || "manual"}
                  onChange={(ev) => setTemplateForm((s) => ({ ...s, triggerAction: ev.target.value }))}
                  data-testid="comms-template-form-trigger-action"
                >
                  <option value="manual">Manual — operator picks this in a campaign</option>
                  <option value="on_approval">Automatically when a pending registration is approved</option>
                </select>
                <span className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 block">
                  Auto-trigger templates fire in response to system events. Only one
                  template per event can hold each non-manual trigger; promoting this
                  template demotes whatever previously held it.
                </span>
              </label>
              <div className="block">
                <span className="text-overline">Body</span>
                <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1 leading-relaxed">
                  Use <strong>Visual</strong> for WYSIWYG, <strong>HTML</strong> to paste designer-supplied
                  markup, <strong>Preview</strong> for a final look. Drag & drop images, paste image URLs,
                  or click <code>+ Banner</code> to insert a hero image at the top. Tokens substituted at
                  send time: <code>{"{{firstName}}"}</code>, <code>{"{{name}}"}</code>,{" "}
                  <code>{"{{email}}"}</code>, <code>{"{{company}}"}</code>, <code>{"{{eventTitle}}"}</code>,{" "}
                  <code>{"{{eventDate}}"}</code>, <code>{"{{eventVenue}}"}</code>.
                </p>
                <div className="mt-2">
                  <Suspense fallback={<div className="text-sm text-[var(--text-tertiary)] py-6 text-center">Loading editor…</div>}>
                    <EmailComposer
                      value={templateForm.body}
                      onChange={(html) => setTemplateForm((s) => ({ ...s, body: html }))}
                      placeholder="Write the email body. Use the toolbar to format text, add lists, links, banner, or images."
                      testId="comms-template-form-body"
                      minHeight={320}
                    />
                  </Suspense>
                </div>
              </div>
            </div>
            <div className="mt-4 border-t border-[var(--border-default)] pt-4">
              <div className="text-overline">Send test email</div>
              <p className="text-[10px] font-mono text-[var(--text-tertiary)] mt-1">
                Sends the current draft (no need to save first) to one inbox. Tokens render
                against a synthetic recipient. Uses the configured email provider.
              </p>
              <div className="mt-2 flex gap-2 flex-wrap">
                <input
                  type="email"
                  className="input-dark flex-1 min-w-[220px]"
                  placeholder="you@example.com"
                  value={testEmail}
                  onChange={(ev) => setTestEmail(ev.target.value)}
                  data-testid="comms-template-test-email"
                  disabled={sendingTest}
                />
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={sendTestEmail}
                  disabled={sendingTest || !testEmail.trim()}
                  data-testid="comms-template-test-send"
                >
                  {sendingTest ? "Sending test…" : "Send test"}
                </button>
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" className="btn-ghost" onClick={() => setTemplateModalOpen(false)} disabled={savingTemplate}>
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={savingTemplate || !templateForm.name.trim()}
                onClick={submitTemplate}
                data-testid="comms-template-form-submit"
              >
                {savingTemplate ? "Saving…" : (templateForm.id ? "Save changes" : "Create template")}
              </button>
            </div>
          </div>
        </div>
      )}
      <MetricDefinitionsModal open={metricsModalOpen} onClose={() => setMetricsModalOpen(false)} />
    </div>
  );
}

function KPICard({ icon: I, k, v }) {
  return (
    <div className="surface border border-[var(--border-default)] p-4 sm:p-5">
      <div className="flex items-center gap-2 text-overline"><I size={11} /> {k}</div>
      <div className="font-mono text-2xl sm:text-3xl mt-2 tabular-nums">{v}</div>
    </div>
  );
}

function StatCard({ label, value, accent, small }) {
  return (
    <div className="surface border border-[var(--border-default)] p-4">
      <div className="text-overline">{label}</div>
      <div
        className={`font-mono mt-2 tabular-nums ${small ? "text-sm" : "text-xl"}`}
        style={accent ? { color: accent } : undefined}
      >
        {value}
      </div>
    </div>
  );
}
